import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { 
  Users, 
  TrendingUp, 
  Brain, 
  Activity, 
  Box, 
  Phone, 
  Mail, 
  MapPin, 
  ChevronDown, 
  ArrowRight,
  Target,
  Award,
  Lightbulb,
  Shield,
  Compass,
  Menu,
  X,
  ClipboardCheck,
  Clock,
  CreditCard,
  CheckCircle,
  AlertCircle,
  FileText,
  Lock,
  Building2,
  Download,
  BarChart3,
  ChevronRight
} from "lucide-react";
import axios from "axios";
import { programDetails } from "@/programData";
import AdminPanel from "@/AdminPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://enrapi.tikabasaapp.com";
const API = `${BACKEND_URL}/api`;
const PAYTR_TEST_MODE = true; // PayTR sandbox mode flag

// Custom Mobile-Friendly Dropdown
const MobileDropdown = ({ value, onChange, options, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const selectedLabel = options.find(o => o === value) || placeholder;

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 border border-gray-300 bg-white text-left flex items-center justify-between"
        style={{ minHeight: '48px', fontSize: '16px', color: value ? '#1a1a1a' : '#9ca3af' }}
        data-testid={`${testId}-trigger`}
      >
        <span>{selectedLabel}</span>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 shadow-lg z-50 max-h-60 overflow-y-auto"
          data-testid={`${testId}-options`}
        >
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full px-4 py-3 text-left text-base hover:bg-gray-100 border-b border-gray-100 last:border-0 ${
                value === opt ? 'bg-blue-50 text-navy font-medium' : 'text-gray-800'
              }`}
              data-testid={`${testId}-option-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Scroll Animation Hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isVisible];
};

// Navbar Component
const Navbar = ({ scrolled, activeSection, setActiveSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);

  const navLinks = [
    { name: "Hakkımızda", href: "#hakkimizda", section: null },
    { 
      name: "Eğitimlerimiz", 
      href: "#egitimlerimiz",
      section: null,
      hasSubmenu: true,
      submenu: [
        { name: "Liderlik ve Yönetim Akademileri", href: "#yonetim-liderlik" },
        { name: "Satış ve Müşteri Deneyimi", href: "#satis" },
        { name: "Deneyim Atölyeleri (Simülasyonlar)", href: "#simulasyonlar" },
        { name: "Organizasyonel Dönüşüm", href: "#kisisel-gelisim" },
        { name: "Çalıştaylar", href: "#calistay" },
        { name: "Online Eğitimler", href: "#online-egitim" },
      ]
    },
    { name: "Testler", href: "#", section: "tests", isButton: true },
    { name: "Eğitim İhtiyaç Analizi", href: "#", section: "needs-analysis", isButton: true },
    { name: "Referanslar", href: "#referanslar", section: null },
    { name: "İletişim", href: "#iletisim", section: null },
  ];

  const handleNavClick = (link, e) => {
    if (link.section) {
      e.preventDefault();
      setActiveSection(link.section);
      setMobileMenuOpen(false);
    } else if (activeSection) {
      setActiveSection(null);
    }
  };

  return (
    <nav 
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-navbar shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#hero" 
            className="flex items-center gap-3" 
            data-testid="logo-link"
            onClick={() => setActiveSection(null)}
          >
            <div>
              <p className="text-white font-semibold text-sm tracking-tight">E&R EĞİTİM</p>
              <p className="text-gold text-xs">Yönetim Danışmanlığı</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.name} className="relative mega-menu-trigger">
                {link.isButton ? (
                  <button
                    onClick={(e) => handleNavClick(link, e)}
                    className={`nav-link text-sm font-medium flex items-center gap-1 transition-colors duration-300 ${
                      activeSection === link.section ? 'text-gold' : 'text-white hover:text-gold'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase()}`}
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    {link.name}
                  </button>
                ) : (
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(link, e)}
                    className="nav-link text-white text-sm font-medium flex items-center gap-1 hover:text-gold transition-colors duration-300"
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                    {link.hasSubmenu && <ChevronDown className="w-4 h-4" />}
                  </a>
                )}
                {link.hasSubmenu && (
                  <div className="mega-menu absolute top-full left-0 pt-4">
                    <div className="bg-white rounded-sm shadow-xl p-4 min-w-[220px]">
                      {link.submenu.map((sublink) => (
                        <a
                          key={sublink.name}
                          href={sublink.href}
                          className="block px-4 py-2 text-navy hover:bg-gray-50 hover:text-gold text-sm transition-colors duration-200"
                        >
                          {sublink.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <a
              href="#iletisim"
              className="btn-gold px-6 py-2.5 text-sm font-semibold"
              data-testid="cta-button"
            >
              Bize Ulaşın
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-navy-dark py-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.isButton ? (
                  <button
                    onClick={(e) => handleNavClick(link, e)}
                    className={`block w-full text-left px-4 py-3 transition-colors duration-200 ${
                      activeSection === link.section ? 'text-gold' : 'text-white hover:text-gold'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      {link.name}
                    </span>
                  </button>
                ) : (
                  <a
                    href={link.href}
                    className="block px-4 py-3 text-white hover:text-gold transition-colors duration-200"
                    onClick={(e) => {
                      handleNavClick(link, e);
                      if (!link.hasSubmenu) setMobileMenuOpen(false);
                      if (link.hasSubmenu) setProgramsOpen(!programsOpen);
                    }}
                  >
                    <span className="flex items-center justify-between">
                      {link.name}
                      {link.hasSubmenu && <ChevronDown className={`w-4 h-4 transition-transform ${programsOpen ? 'rotate-180' : ''}`} />}
                    </span>
                  </a>
                )}
                {link.hasSubmenu && programsOpen && (
                  <div className="pl-8 pb-2">
                    {link.submenu.map((sublink) => (
                      <a
                        key={sublink.name}
                        href={sublink.href}
                        className="block py-2 text-gray-300 hover:text-gold text-sm"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {sublink.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="px-4 pt-4">
              <a href="#iletisim" className="btn-gold block text-center py-3 font-semibold">
                Bize Ulaşın
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Tests Page Component
const TestsPage = ({ onBack }) => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [testSession, setTestSession] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchTests();
    // Check URL params for PayTR return redirect
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment');
    const oid = params.get('oid');
    if (paymentResult === 'success' && oid) {
      // User returned from PayTR payment - confirm and show access code
      (async () => {
        try {
          const response = await axios.post(`${API}/payments/confirm/${oid}`);
          if (response.data.status === 'success' && response.data.access_code) {
            setPaymentStatus({
              status: 'success',
              access_code: response.data.access_code,
              merchant_oid: oid
            });
          }
        } catch (err) {
          console.error('Payment confirm error:', err);
        }
      })();
      // Clean URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentResult === 'failed' && oid) {
      // Payment failed
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchTests = async () => {
    try {
      // Get tests first
      const response = await axios.get(`${API}/tests`);
      // Only seed if no tests exist
      if (response.data.length === 0) {
        await axios.post(`${API}/tests/seed`);
        const newResponse = await axios.get(`${API}/tests`);
        setTests(newResponse.data);
      } else {
        setTests(response.data);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessTest = async (codeOverride) => {
    const code = (codeOverride || accessCode || "").trim();
    if (!code) {
      alert("Geçersiz erişim kodu");
      return;
    }
    try {
      const response = await axios.get(`${API}/tests/access/${code}`);
      setAccessCode(code);
      setTestSession(response.data);
      setShowAccessForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Geçersiz erişim kodu');
    }
  };

  const handleViewResults = async (codeOverride) => {
    const code = (codeOverride || accessCode || "").trim();
    if (!code) {
      alert("Geçersiz erişim kodu");
      return;
    }
    try {
      const response = await axios.get(`${API}/tests/results/${code}`);
      setAccessCode(code);
      setTestResults(response.data);
      setShowAccessForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Sonuçlar bulunamadı');
    }
  };

  if (testResults) {
    return <TestResultsView results={testResults} accessCode={accessCode} onBack={() => setTestResults(null)} />;
  }

  if (testSession) {
    return <TestTakingView session={testSession} accessCode={accessCode} onComplete={(results) => {
      setTestResults({ test: testSession.test, result: results });
      setTestSession(null);
    }} />;
  }

  if (showPayment && selectedTest) {
    return <PaymentForm test={selectedTest} onBack={() => setShowPayment(false)} onSuccess={(result) => {
      setPaymentStatus(result);
      setShowPayment(false);
      setSelectedTest(null);
    }} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
            data-testid="back-to-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Ana Sayfaya Dön
          </button>
          
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-gold text-sm font-semibold tracking-widest uppercase mb-2">
                Online Değerlendirme
              </p>
              <h1 className="text-3xl lg:text-4xl font-bold text-navy tracking-tight">
                Profesyonel Testler
              </h1>
              <p className="text-gray-600 mt-2 max-w-2xl">
                Liderlik, yönetim ve satış becerilerinizi ölçen profesyonel değerlendirme araçları. 
                Online olarak satın alın, hemen çözün ve detaylı raporunuzu alın.
              </p>
            </div>
            
            <button
              onClick={() => setShowAccessForm(true)}
              className="btn-primary px-6 py-3 flex items-center gap-2 whitespace-nowrap"
              data-testid="access-test-btn"
            >
              <Lock className="w-5 h-5" />
              Erişim Koduyla Giriş
            </button>
          </div>
        </div>

        {/* Payment Success Message */}
        {paymentStatus && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8 animate-fade-in">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">Ödeme Başarılı!</h3>
                <p className="text-green-700 mb-4">
                  Testinize erişim kodunuz: <span className="font-mono font-bold text-xl">{paymentStatus.access_code}</span>
                </p>
                <p className="text-green-600 text-sm mb-4">
                  Bu kodu saklayın. Test çözmek ve sonuçlarınıza erişmek için kullanacaksınız.
                </p>
                <button
                  onClick={() => handleAccessTest(paymentStatus.access_code)}
                  className="btn-gold px-6 py-2"
                >
                  Teste Başla
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Access Code Modal */}
        {showAccessForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-8 max-w-md w-full animate-scale-in">
              <h3 className="text-xl font-bold text-navy mb-4">Erişim Kodu Girin</h3>
              <p className="text-gray-600 mb-6 text-sm">
                Satın aldığınız testin erişim kodunu girerek teste başlayabilir veya sonuçlarınızı görüntüleyebilirsiniz.
              </p>
              <input
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                placeholder="Erişim Kodu"
                className="w-full px-4 py-3 border border-gray-200 mb-4 font-mono text-lg tracking-widest text-center"
                data-testid="access-code-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleAccessTest}
                  className="flex-1 btn-gold py-3 font-semibold"
                  data-testid="start-test-btn"
                >
                  Teste Başla
                </button>
                <button
                  onClick={handleViewResults}
                  className="flex-1 btn-primary py-3 font-semibold"
                  data-testid="view-results-btn"
                >
                  Sonuçları Gör
                </button>
              </div>
              <button
                onClick={() => setShowAccessForm(false)}
                className="w-full mt-4 text-gray-500 hover:text-navy transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        )}

        {/* Tests Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tests.map((test, index) => (
              <div 
                key={test.id}
                className="bg-white border border-gray-100 overflow-hidden card-hover animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`test-card-${index}`}
              >
                <div className="relative h-48">
                  <img 
                    src={test.image_url} 
                    alt={test.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-gold text-white text-xs font-semibold px-3 py-1">
                      {test.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-navy mb-2">{test.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{test.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {test.duration_minutes} dk
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {test.question_count} soru
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      {test.discount_percent > 0 ? (
                        <>
                          <span className="text-2xl font-bold text-navy">
                            ₺{(test.final_price ?? test.price).toFixed(2)}
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            ₺{test.price.toFixed(2)}
                          </span>
                          <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            %{Math.round(test.discount_percent)} İndirim
                          </span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-navy">
                          ₺{test.price.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTest(test);
                        setShowPayment(true);
                      }}
                      className="btn-gold px-4 py-2 text-sm flex items-center gap-2"
                      data-testid={`buy-test-${index}`}
                    >
                      <CreditCard className="w-4 h-4" />
                      Satın Al
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 bg-navy p-8 lg:p-12">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-8 h-8 text-gold" />
              </div>
              <h4 className="text-white font-semibold mb-2">Güvenli Ödeme</h4>
              <p className="text-gray-400 text-sm">PayTR altyapısı ile güvenli ödeme</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClipboardCheck className="w-8 h-8 text-gold" />
              </div>
              <h4 className="text-white font-semibold mb-2">Anında Erişim</h4>
              <p className="text-gray-400 text-sm">Ödeme sonrası hemen teste başlayın</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gold" />
              </div>
              <h4 className="text-white font-semibold mb-2">Detaylı Rapor</h4>
              <p className="text-gray-400 text-sm">Kişiselleştirilmiş sonuç raporu</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Form Component
const PaymentForm = ({ test, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iframeToken, setIframeToken] = useState(null);
  const [merchantOid, setMerchantOid] = useState(null);
  const [paymentStep, setPaymentStep] = useState('form'); // form, iframe, polling
  const iframeLoadCount = useRef(0);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    customer_type: 'individual', // 'individual' | 'corporate'
    tc_no: '',
    company_name: '',
    tax_number: '',
    tax_office: '',
    address: '',
    city: '',
    zip_code: '',
  });

  // Poll for payment status when in iframe mode
  useEffect(() => {
    if (paymentStep !== 'iframe' || !merchantOid) return;

    let pendingCount = 0; // count consecutive pending responses

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/payments/check/${merchantOid}`);
        if (response.data.status === 'success') {
          clearInterval(pollInterval);
          onSuccess({
            status: 'success',
            access_code: response.data.access_code,
            merchant_oid: merchantOid
          });
        } else if (response.data.status === 'failed') {
          clearInterval(pollInterval);
          setError('Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.');
          setPaymentStep('form');
          setIframeToken(null);
        } else {
          // Still pending. After ~15s of pending, proactively call confirm.
          pendingCount += 1;
          if (pendingCount >= 5) {
            try {
              const confirmRes = await axios.post(`${API}/payments/confirm/${merchantOid}`);
              if (confirmRes.data.status === 'success' && confirmRes.data.access_code) {
                clearInterval(pollInterval);
                onSuccess({
                  status: 'success',
                  access_code: confirmRes.data.access_code,
                  merchant_oid: merchantOid
                });
              }
            } catch (e) {
              // keep polling silently
            }
          }
        }
      } catch (err) {
        // Silently continue polling
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [paymentStep, merchantOid, onSuccess]);

  // Listen for postMessage from PayTR iframe (success/fail page)
  useEffect(() => {
    if (paymentStep !== 'iframe' || !merchantOid) return;

    const handleMessage = async (event) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'PAYTR_PAYMENT_SUCCESS' && data.merchant_oid === merchantOid) {
        // Payment confirmed via iframe redirect - call confirm endpoint
        try {
          const response = await axios.post(`${API}/payments/confirm/${merchantOid}`);
          if (response.data.status === 'success' && response.data.access_code) {
            onSuccess({
              status: 'success',
              access_code: response.data.access_code,
              merchant_oid: merchantOid
            });
          }
        } catch (err) {
          console.error('Confirm payment error:', err);
        }
      } else if (data.type === 'PAYTR_PAYMENT_FAILED') {
        setError('Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.');
        setPaymentStep('form');
        setIframeToken(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [paymentStep, merchantOid, onSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/payments/initialize`, {
        test_id: test.id,
        buyer: {
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          phone: formData.phone,
          customer_type: formData.customer_type,
          tc_no: formData.tc_no,
          company_name: formData.company_name,
          tax_number: formData.tax_number,
          tax_office: formData.tax_office,
          address: formData.address,
          city: formData.city,
          zip_code: formData.zip_code,
        }
      });

      if (response.data.status === 'success' && response.data.iframe_token) {
        // Show PayTR iframe for payment
        setIframeToken(response.data.iframe_token);
        setMerchantOid(response.data.merchant_oid);
        setPaymentStep('iframe');
      } else {
        setError(response.data.error_message || 'Ödeme başlatılamadı. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg || JSON.stringify(d)).join(', '));
      } else {
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show PayTR iframe
  if (paymentStep === 'iframe' && iframeToken) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => {
              setPaymentStep('form');
              setIframeToken(null);
              setMerchantOid(null);
            }}
            className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Geri Dön
          </button>

          <div className="bg-white border border-gray-100 p-6 mb-4">
            <div className="flex items-start gap-4">
              <img 
                src={test.image_url} 
                alt={test.name}
                className="w-16 h-16 object-cover rounded"
              />
              <div className="flex-1">
                <h2 className="text-lg font-bold text-navy">{test.name}</h2>
                <p className="text-gray-500 text-sm">{test.duration_minutes} dk • {test.question_count} soru</p>
              </div>
              <div className="text-right">
                {test.discount_percent > 0 ? (
                  <>
                    <p className="text-2xl font-bold text-navy">₺{(test.final_price ?? test.price).toFixed(2)}</p>
                    <p className="text-xs text-gray-400 line-through">₺{test.price.toFixed(2)}</p>
                    <p className="text-xs font-semibold text-red-600">%{Math.round(test.discount_percent)} indirim</p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-navy">₺{test.price.toFixed(2)}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-100 overflow-hidden">
            <div className="bg-navy px-6 py-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Güvenli Ödeme
              </h3>
            </div>
            <iframe
              src={`https://www.paytr.com/odeme/guvenli/${iframeToken}`}
              id="paytriframe"
              frameBorder="0"
              scrolling="yes"
              style={{ width: '100%', height: '600px' }}
              title="PayTR Ödeme"
              onLoad={async () => {
                iframeLoadCount.current += 1;
                // 1st load = PayTR payment form. 2nd+ load = redirect to our /api/payment-success.
                // After redirect, immediately call confirm to get access code.
                if (iframeLoadCount.current >= 2 && merchantOid) {
                  try {
                    const response = await axios.post(`${API}/payments/confirm/${merchantOid}`);
                    if (response.data.status === 'success' && response.data.access_code) {
                      onSuccess({
                        status: 'success',
                        access_code: response.data.access_code,
                        merchant_oid: merchantOid
                      });
                    }
                  } catch (e) {
                    // fall through; polling/postMessage will handle it
                  }
                }
              }}
            />
          </div>

          {PAYTR_TEST_MODE && (
            <div className="mt-3 bg-yellow-50 border border-yellow-200 p-4 rounded">
              <p className="text-yellow-800 text-sm">
                <strong>Test Modu:</strong> PayTR test kartları kullanabilirsiniz: 
                <br />• 4355 0843 5508 4358 (Ay: 12, Yıl: 30, CVC: 000)
                <br />• 5406 6754 0667 5403 (Ay: 12, Yıl: 30, CVC: 000)
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Geri Dön
        </button>

        <div className="bg-white border border-gray-100 p-8">
          <div className="flex items-start gap-6 mb-8 pb-8 border-b border-gray-100">
            <img 
              src={test.image_url} 
              alt={test.name}
              className="w-24 h-24 object-cover rounded"
            />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-navy mb-2">{test.name}</h2>
              <p className="text-gray-600 text-sm mb-2">{test.description}</p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span>{test.duration_minutes} dk</span>
                <span>{test.question_count} soru</span>
              </div>
            </div>
            <div className="text-right">
              {test.discount_percent > 0 ? (
                <>
                  <p className="text-3xl font-bold text-navy">₺{(test.final_price ?? test.price).toFixed(2)}</p>
                  <p className="text-sm text-gray-400 line-through">₺{test.price.toFixed(2)}</p>
                  <p className="text-xs font-semibold text-red-600 mt-0.5">%{Math.round(test.discount_percent)} indirim uygulandi</p>
                </>
              ) : (
                <p className="text-3xl font-bold text-navy">₺{test.price.toFixed(2)}</p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="payment-form">
            {/* Buyer Info */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-navy mb-4">Kişisel Bilgiler</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Ad *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-buyer-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Soyad *</label>
                  <input
                    type="text"
                    required
                    value={formData.surname}
                    onChange={(e) => setFormData({...formData, surname: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-buyer-surname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">E-posta *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-buyer-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Telefon *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="05XX XXX XXXX"
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-buyer-phone"
                  />
                </div>
              </div>
            </div>

            {/* Invoice / Faturalama Bilgileri */}
            <div className="bg-white border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-navy mb-4">Faturalama Bilgileri</h3>

              <div className="mb-5">
                <label className="block text-sm font-medium text-navy mb-2">Fatura Tipi *</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, customer_type: 'individual'})}
                    className={`flex-1 px-4 py-3 border text-sm font-medium transition-colors ${formData.customer_type === 'individual' ? 'border-gold bg-gold/10 text-navy' : 'border-gray-200 text-gray-600 hover:border-gold/50'}`}
                    data-testid="customer-type-individual"
                  >
                    Bireysel (Gerçek Kişi)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, customer_type: 'corporate'})}
                    className={`flex-1 px-4 py-3 border text-sm font-medium transition-colors ${formData.customer_type === 'corporate' ? 'border-gold bg-gold/10 text-navy' : 'border-gray-200 text-gray-600 hover:border-gold/50'}`}
                    data-testid="customer-type-corporate"
                  >
                    Kurumsal (Tüzel Kişi)
                  </button>
                </div>
              </div>

              {formData.customer_type === 'individual' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-navy mb-2">TC Kimlik No *</label>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    pattern="\d{11}"
                    maxLength={11}
                    value={formData.tc_no}
                    onChange={(e) => setFormData({...formData, tc_no: e.target.value.replace(/\D/g, '')})}
                    placeholder="11 haneli TC Kimlik Numarası"
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-tc-no"
                  />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy mb-2">Şirket Ünvanı *</label>
                    <input
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Örn: ENR Eğitim ve Danışmanlık A.Ş."
                      className="w-full px-4 py-3 border border-gray-200"
                      data-testid="input-company-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Vergi Numarası *</label>
                    <input
                      type="text"
                      required
                      inputMode="numeric"
                      maxLength={11}
                      value={formData.tax_number}
                      onChange={(e) => setFormData({...formData, tax_number: e.target.value.replace(/\D/g, '')})}
                      placeholder="10 veya 11 haneli vergi no"
                      className="w-full px-4 py-3 border border-gray-200"
                      data-testid="input-tax-number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-2">Vergi Dairesi *</label>
                    <input
                      type="text"
                      required
                      value={formData.tax_office}
                      onChange={(e) => setFormData({...formData, tax_office: e.target.value})}
                      placeholder="Örn: Beşiktaş"
                      className="w-full px-4 py-3 border border-gray-200"
                      data-testid="input-tax-office"
                    />
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-navy mb-2">Fatura Adresi</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Mah. Cad. No. Daire"
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">İl</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="İstanbul"
                    className="w-full px-4 py-3 border border-gray-200"
                    data-testid="input-city"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 p-4 mb-6 rounded">
              <p className="text-gray-700 text-sm flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-gold" />
                Kart bilgileriniz güvenli PayTR ödeme sayfasında alınacaktır.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 p-4 mb-6 rounded flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-4 font-semibold flex items-center justify-center gap-2"
              data-testid="submit-payment"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  İşleniyor...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Ödeme Sayfasına Geç
                </>
              )}
            </button>

            <p className="text-center text-gray-500 text-xs mt-4">
              Ödeme işlemi PayTR güvenli altyapısı ile gerçekleştirilmektedir.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

// Test Taking View Component
const TestTakingView = ({ session, accessCode, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { test, questions } = session;
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (optionIndex) => {
    setAnswers(prev => ({...prev, [question.id]: optionIndex}));
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      // Jump user to first unanswered question and prevent submit
      const firstUnansweredIdx = questions.findIndex(q => answers[q.id] === undefined);
      setCurrentQuestion(firstUnansweredIdx);
      alert(`Lütfen tüm soruları yanıtlayın. ${unanswered.length} soru cevaplanmamış.`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/tests/submit`, {
        access_code: accessCode,
        answers: answers
      });
      onComplete(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const isCurrentAnswered = answers[question.id] !== undefined;
  const unansweredCount = questions.filter(q => answers[q.id] === undefined).length;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-navy">{test.name}</h2>
            <span className="text-gray-500 text-sm">
              Soru {currentQuestion + 1} / {questions.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
            <div 
              className="bg-gold h-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="bg-white border border-gray-100 p-8 mb-6">
          <h3 className="text-lg font-semibold text-navy mb-6">
            {question.question_text}
          </h3>
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                className={`w-full text-left p-4 border-2 transition-all duration-200 ${
                  answers[question.id] === index
                    ? 'border-gold bg-gold/5 text-navy'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`option-${index}`}
              >
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 text-sm font-semibold ${
                  answers[question.id] === index
                    ? 'bg-gold text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {String.fromCharCode(65 + index)}
                </span>
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            className="px-6 py-3 border border-gray-200 text-navy hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Önceki
          </button>
          
          {currentQuestion < questions.length - 1 ? (
            <button
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              disabled={!isCurrentAnswered}
              className="btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="next-question-btn"
              title={!isCurrentAnswered ? "Bu soruyu cevapladıktan sonra geçebilirsiniz" : ""}
            >
              Sonraki
            </button>
          ) : (
            <div className="flex flex-col items-end gap-2">
              {unansweredCount > 0 && (
                <span className="text-xs text-red-600 font-medium" data-testid="unanswered-warning">
                  {unansweredCount} soru cevaplanmamış
                </span>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting || unansweredCount > 0}
                className="btn-gold px-8 py-3 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-test"
                title={unansweredCount > 0 ? `Tüm soruları cevaplayın` : "Testi gönder"}
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Testi Tamamla
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Question Navigator */}
        <div className="mt-8 bg-white border border-gray-100 p-6">
          <h4 className="text-sm font-semibold text-navy mb-4">Soru Navigasyonu</h4>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(index)}
                className={`w-10 h-10 text-sm font-medium transition-all ${
                  currentQuestion === index
                    ? 'bg-navy text-white'
                    : answers[q.id] !== undefined
                    ? 'bg-gold/20 text-gold border border-gold'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Test Results View Component
const TestResultsView = ({ results, accessCode, onBack }) => {
  const { test, result } = results;
  const report = result.report;
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const getLevelColor = (level) => {
    switch(level) {
      case 'Üstün': return 'text-green-600 bg-green-100';
      case 'İyi': return 'text-blue-600 bg-blue-100';
      case 'Orta': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-red-600 bg-red-100';
    }
  };

  const handleDownloadTestPDF = async () => {
    if (!accessCode) {
      alert('PDF indirmek için erişim kodu gereklidir.');
      return;
    }
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/tests/report-pdf/${accessCode}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ENR_Test_Analiz_Raporu.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('PDF indirirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Testlere Dön
        </button>

        {/* Score Card */}
        <div className="bg-white border border-gray-100 p-8 mb-6 text-center">
          <h2 className="text-2xl font-bold text-navy mb-2">{test.name}</h2>
          <p className="text-gray-500 mb-6">Test Sonuçlarınız</p>
          
          <div className="inline-flex items-center justify-center w-40 h-40 rounded-full bg-navy mb-6">
            <div className="text-center">
              <span className="text-4xl font-bold text-gold">{result.score}</span>
              <span className="block text-white text-sm">puan</span>
            </div>
          </div>

          <div className={`inline-block px-6 py-2 rounded-full font-semibold ${getLevelColor(report.level)}`}>
            {report.level}
          </div>

          <p className="text-gray-600 mt-4 max-w-md mx-auto">
            {report.level_description}
          </p>
        </div>

        {/* Summary */}
        <div className="bg-white border border-gray-100 p-8 mb-6">
          <h3 className="text-lg font-semibold text-navy mb-4">Özet</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-gray-50 p-4">
              <p className="text-3xl font-bold text-navy">{result.total_questions}</p>
              <p className="text-gray-500 text-sm">Toplam Soru</p>
            </div>
            <div className="bg-green-50 p-4">
              <p className="text-3xl font-bold text-green-600">{result.correct_answers}</p>
              <p className="text-gray-500 text-sm">Doğru Cevap</p>
            </div>
            <div className="bg-red-50 p-4">
              <p className="text-3xl font-bold text-red-600">{result.total_questions - result.correct_answers}</p>
              <p className="text-gray-500 text-sm">Yanlış Cevap</p>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white border border-gray-100 p-8 mb-6">
          <h3 className="text-lg font-semibold text-navy mb-4">Önerilen Eğitimler</h3>
          <p className="text-gray-600 mb-6">
            Sonuçlarınıza göre aşağıdaki eğitim programlarımızı değerlendirmenizi öneririz:
          </p>
          <ul className="space-y-3">
            {report.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <span className="text-navy">{rec}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PDF Download & Navigation */}
        <div className="bg-white border border-gray-100 p-8">
          <h3 className="text-lg font-semibold text-navy mb-4">Detaylı Analiz Raporu</h3>
          <p className="text-gray-600 mb-6">
            Test sonuçlarınıza göre hazırlanan detaylı analiz raporunu PDF olarak indirebilirsiniz. 
            Rapor; güçlü yönlerinizi, gelişim alanlarınızı ve kişiselleştirilmiş tavsiyelerinizi içermektedir.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={handleDownloadTestPDF}
              disabled={downloadingPdf}
              className="btn-gold px-6 py-3 inline-flex items-center justify-center gap-2"
              data-testid="download-test-pdf-btn"
            >
              {downloadingPdf ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  İndiriliyor...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  PDF Analiz Raporu İndir
                </>
              )}
            </button>
            <button 
              onClick={onBack}
              className="btn-primary px-6 py-3 inline-flex items-center justify-center gap-2"
              data-testid="back-to-tests-btn"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Testlere Dön
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Needs Analysis Page Component
const NeedsAnalysisPage = ({ onBack }) => {
  const [step, setStep] = useState('intro'); // intro, company, departments, questions, results
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    sector: '',
    employee_count: '',
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: ''
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);

  const allDepartments = [
    { id: "Liderlik", name: "Liderlik", icon: "👑", description: "Yönetsel liderlik ve ekip yönetimi" },
    { id: "Satış", name: "Satış", icon: "📈", description: "Satış süreçleri ve müşteri kazanımı" },
    { id: "İletişim", name: "İletişim", icon: "💬", description: "Kurum içi ve dışı iletişim" },
    { id: "Takım", name: "Takım", icon: "👥", description: "Takım çalışması ve işbirliği" },
    { id: "Strateji", name: "Strateji", icon: "🎯", description: "Stratejik planlama ve hedef yönetimi" },
    { id: "Yönetim", name: "Yönetim", icon: "⚙️", description: "Performans ve süreç yönetimi" },
    { id: "Kişisel Gelişim", name: "Kişisel Gelişim", icon: "⭐", description: "Bireysel yetkinlik gelişimi" },
    { id: "Müşteri İlişkileri", name: "Müşteri İlişkileri", icon: "❤️", description: "Müşteri memnuniyeti ve sadakati" },
    { id: "Kurumsal Gelişim", name: "Kurumsal Gelişim", icon: "🏢", description: "Kurum kültürü ve organizasyonel gelişim" },
  ];

  const fetchQuestions = async (departments) => {
    try {
      const deptParam = departments.join(',');
      const response = await axios.get(`${API}/needs-analysis/questions?departments=${encodeURIComponent(deptParam)}`);
      setQuestions(response.data);
      setCurrentQuestion(0);
      setAnswers({});
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    if (!companyInfo.sector || !companyInfo.employee_count) {
      alert('Lütfen Sektör ve Çalışan Sayısı alanlarını seçiniz.');
      return;
    }
    setStep('departments');
  };

  const toggleDepartment = (deptId) => {
    setSelectedDepartments(prev => {
      if (prev.includes(deptId)) {
        return prev.filter(d => d !== deptId);
      }
      return [...prev, deptId];
    });
  };

  const handleDepartmentsSubmit = async () => {
    if (selectedDepartments.length === 0) {
      alert('Lütfen en az bir departman seçiniz.');
      return;
    }
    setLoading(true);
    await fetchQuestions(selectedDepartments);
    setLoading(false);
    setStep('questions');
  };

  const handleAnswer = (questionId, optionIndex) => {
    setAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: optionIndex };
      return newAnswers;
    });
    // Auto-advance to next question after answering
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => Math.min(prev + 1, questions.length - 1)), 300);
    }
  };

  const handleSubmitAnalysis = async () => {
    // Check if enough questions answered
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < questions.length) {
      const unanswered = questions.filter(q => answers[q.id] === undefined).map((q, i) => {
        const idx = questions.findIndex(x => x.id === q.id);
        return idx + 1;
      });
      alert(`Lütfen tüm soruları cevaplayın. Cevaplanmamış sorular: ${unanswered.join(', ')}`);
      // Navigate to first unanswered question
      const firstUnanswered = questions.findIndex(q => answers[q.id] === undefined);
      if (firstUnanswered >= 0) setCurrentQuestion(firstUnanswered);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/needs-analysis/submit`, {
        company_info: companyInfo,
        answers: answers,
        departments: selectedDepartments
      });
      setResults(response.data);
      setAnalysisId(response.data.id);
      setStep('results');
    } catch (error) {
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (analysisId) {
      window.open(`${API}/needs-analysis/report/${analysisId}`, '_blank');
    }
  };

  const sectors = [
    "Otomotiv", "Finans & Bankacılık", "Perakende", "Üretim", "Teknoloji",
    "Sağlık", "Enerji", "İnşaat", "Telekomünikasyon", "Lojistik",
    "Gıda", "Tekstil", "İlaç", "Danışmanlık", "Eğitim", "Diğer"
  ];

  const employeeCounts = [
    "1-50", "51-100", "101-250", "251-500", "501-1000", "1000+"
  ];

  // Intro Step
  if (step === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16" data-testid="needs-analysis-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
            data-testid="back-to-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Ana Sayfaya Dön
          </button>

          <div className="bg-white border border-gray-100 p-8 lg:p-12">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-gold" />
              </div>
              <p className="text-gold text-sm font-semibold tracking-widest uppercase mb-2">
                Ücretsiz Kurumsal Değerlendirme
              </p>
              <h1 className="text-3xl lg:text-4xl font-bold text-navy tracking-tight mb-4">
                Eğitim İhtiyaç Analizi
              </h1>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Kurumunuzun eğitim ihtiyaçlarını belirleyin. 30 soruluk kapsamlı analizimiz ile 
                hangi alanlarda gelişime ihtiyacınız olduğunu keşfedin ve size özel eğitim 
                önerileri alın.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 text-gold" />
                </div>
                <h4 className="font-semibold text-navy mb-2">5-10 Dakika</h4>
                <p className="text-gray-500 text-sm">Hızlı ve kolay değerlendirme</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-gold" />
                </div>
                <h4 className="font-semibold text-navy mb-2">PDF Rapor</h4>
                <p className="text-gray-500 text-sm">Detaylı analiz raporu</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-gold" />
                </div>
                <h4 className="font-semibold text-navy mb-2">Kişisel Öneriler</h4>
                <p className="text-gray-500 text-sm">Size özel eğitim programları</p>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setStep('company')}
                className="btn-gold px-10 py-4 text-lg font-semibold inline-flex items-center gap-3"
                data-testid="start-analysis-btn"
              >
                Analize Başla
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Company Info Step
  if (step === 'company') {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => setStep('intro')}
            className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Geri
          </button>

          <div className="bg-white border border-gray-100 p-8">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-navy">Kurumsal Bilgiler</h2>
                <p className="text-gray-500 text-sm">Size özel analiz için bilgilerinizi girin</p>
              </div>
            </div>

            <form onSubmit={handleCompanySubmit} className="space-y-5" data-testid="company-form">
              <div>
                <label className="block text-sm font-medium text-navy mb-2">Şirket Adı *</label>
                <input
                  type="text"
                  required
                  value={companyInfo.company_name}
                  onChange={(e) => setCompanyInfo({...companyInfo, company_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200"
                  placeholder="Şirket adınız"
                  data-testid="input-company-name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Sektör *</label>
                  <MobileDropdown
                    value={companyInfo.sector}
                    onChange={(val) => setCompanyInfo({...companyInfo, sector: val})}
                    options={sectors}
                    placeholder="Seçiniz"
                    testId="input-sector"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Çalışan Sayısı *</label>
                  <MobileDropdown
                    value={companyInfo.employee_count}
                    onChange={(val) => setCompanyInfo({...companyInfo, employee_count: val})}
                    options={employeeCounts}
                    placeholder="Seçiniz"
                    testId="input-employee-count"
                  />
                </div>
              </div>

              <div className="border-t border-gray-100 pt-5 mt-5">
                <h4 className="font-medium text-navy mb-4">İletişim Bilgileri</h4>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Ad Soyad *</label>
                  <input
                    type="text"
                    required
                    value={companyInfo.contact_name}
                    onChange={(e) => setCompanyInfo({...companyInfo, contact_name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    placeholder="Adınız Soyadınız"
                    data-testid="input-contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Ünvan *</label>
                  <input
                    type="text"
                    required
                    value={companyInfo.contact_title}
                    onChange={(e) => setCompanyInfo({...companyInfo, contact_title: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    placeholder="örn: İK Müdürü"
                    data-testid="input-contact-title"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">E-posta *</label>
                  <input
                    type="email"
                    required
                    value={companyInfo.contact_email}
                    onChange={(e) => setCompanyInfo({...companyInfo, contact_email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    placeholder="email@sirket.com"
                    data-testid="input-contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">Telefon *</label>
                  <input
                    type="tel"
                    required
                    value={companyInfo.contact_phone}
                    onChange={(e) => setCompanyInfo({...companyInfo, contact_phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200"
                    placeholder="+90 5XX XXX XXXX"
                    data-testid="input-contact-phone"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full btn-gold py-4 font-semibold flex items-center justify-center gap-2"
                data-testid="company-submit-btn"
              >
                Departman Seçimine Geç
                <ChevronRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Department Selection Step
  if (step === 'departments') {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => setStep('company')}
            className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Geri
          </button>

          <div className="bg-white border border-gray-100 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center">
                <Target className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-navy">Departman Seçimi</h2>
                <p className="text-gray-500 text-sm">Analiz etmek istediğiniz departmanları seçin</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Aşağıdaki departmanlardan değerlendirmek istediğinizi seçin. Sadece seçtiğiniz departmanlarla ilgili sorular gelecektir.
              <span className="text-red-500 font-medium"> (En az 1 departman seçimi zorunludur)</span>
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              {allDepartments.map((dept) => {
                const isSelected = selectedDepartments.includes(dept.id);
                return (
                  <button
                    key={dept.id}
                    onClick={() => toggleDepartment(dept.id)}
                    className={`text-left p-4 border-2 rounded-lg transition-all duration-200 ${
                      isSelected 
                        ? 'border-gold bg-gold/5 shadow-sm' 
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`dept-${dept.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">{dept.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className={`font-semibold text-sm ${isSelected ? 'text-navy' : 'text-gray-700'}`}>
                            {dept.name}
                          </h4>
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-gray-500 text-xs mt-1 line-clamp-2">{dept.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedDepartments.length > 0 && (
              <div className="bg-navy/5 border border-navy/10 rounded-lg p-4 mb-6">
                <p className="text-sm text-navy">
                  <span className="font-semibold">{selectedDepartments.length} departman</span> seçildi: {selectedDepartments.join(', ')}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedDepartments(allDepartments.map(d => d.id));
                }}
                className="btn-primary px-6 py-3 text-sm"
                data-testid="select-all-depts"
              >
                Tümünü Seç
              </button>
              {selectedDepartments.length > 0 && (
                <button
                  onClick={() => setSelectedDepartments([])}
                  className="px-6 py-3 text-sm border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                  data-testid="clear-all-depts"
                >
                  Temizle
                </button>
              )}
              <button
                onClick={handleDepartmentsSubmit}
                disabled={selectedDepartments.length === 0 || loading}
                className={`ml-auto px-8 py-3 font-semibold flex items-center gap-2 transition-all ${
                  selectedDepartments.length === 0 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'btn-gold'
                }`}
                data-testid="departments-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    Sorulara Geç
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Questions Step
  if (step === 'questions' && questions.length > 0) {
    const question = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const allAnswered = Object.keys(answers).length === questions.length;

    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white border border-gray-100 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-navy">Eğitim İhtiyaç Analizi</h2>
              <span className="text-gray-500 text-sm">
                Soru {currentQuestion + 1} / {questions.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
              <div 
                className="bg-gold h-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="bg-white border border-gray-100 p-8 mb-6">
            <span className="inline-block bg-navy/10 text-navy text-xs font-semibold px-3 py-1 mb-4">
              {question.category}
            </span>
            <h3 className="text-lg font-semibold text-navy mb-6">
              {question.question}
            </h3>
            <div className="space-y-3">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(question.id, index)}
                  className={`w-full text-left p-4 border-2 transition-all duration-200 ${
                    answers[question.id] === index
                      ? 'border-gold bg-gold/5 text-navy'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`option-${index}`}
                >
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full mr-3 text-sm font-semibold ${
                    answers[question.id] === index
                      ? 'bg-gold text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {index + 1}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
              disabled={currentQuestion === 0}
              className="px-6 py-3 border border-gray-200 text-navy hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Önceki
            </button>
            
            {currentQuestion < questions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                className="btn-primary px-6 py-3"
              >
                Sonraki
              </button>
            ) : (
              <button
                onClick={handleSubmitAnalysis}
                disabled={loading}
                className="btn-gold px-8 py-3 flex items-center gap-2 disabled:opacity-50"
                data-testid="submit-analysis"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analiz Ediliyor...
                  </>
                ) : (
                  <>
                    <BarChart3 className="w-5 h-5" />
                    Analizi Tamamla
                  </>
                )}
              </button>
            )}
          </div>

          {/* Question Navigator */}
          <div className="mt-8 bg-white border border-gray-100 p-6">
            <h4 className="text-sm font-semibold text-navy mb-4">Soru Navigasyonu</h4>
            <div className="flex flex-wrap gap-2">
              {questions.map((q, index) => (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestion(index)}
                  className={`w-10 h-10 text-sm font-medium transition-all ${
                    currentQuestion === index
                      ? 'bg-navy text-white'
                      : answers[q.id] !== undefined
                      ? 'bg-gold/20 text-gold border border-gold'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Step
  if (step === 'results' && results) {
    const getScoreColor = (score) => {
      if (score >= 80) return 'text-green-600 bg-green-100';
      if (score >= 60) return 'text-blue-600 bg-blue-100';
      if (score >= 40) return 'text-yellow-600 bg-yellow-100';
      return 'text-red-600 bg-red-100';
    };

    const getScoreLabel = (score) => {
      if (score >= 80) return 'Mükemmel';
      if (score >= 60) return 'İyi';
      if (score >= 40) return 'Gelişime Açık';
      return 'Kritik';
    };

    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-6"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Ana Sayfaya Dön
          </button>

          {/* Header */}
          <div className="bg-white border border-gray-100 p-8 mb-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-navy mb-2">Analiz Tamamlandı!</h2>
            <p className="text-gray-600 mb-6">
              {companyInfo.company_name} için eğitim ihtiyaç analizi sonuçları
            </p>
            <button
              onClick={handleDownloadPDF}
              className="btn-gold px-6 py-3 inline-flex items-center gap-2"
              data-testid="download-pdf-btn"
            >
              <Download className="w-5 h-5" />
              PDF Raporu İndir
            </button>
          </div>

          {/* Scores */}
          <div className="bg-white border border-gray-100 p-8 mb-6">
            <h3 className="text-lg font-semibold text-navy mb-6">Kategori Bazlı Puanlar</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(results.scores).map(([category, score]) => (
                <div key={category} className="border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-navy">{category}</span>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${getScoreColor(score)}`}>
                      {getScoreLabel(score)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 h-3 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          score >= 60 ? 'bg-green-500' : score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${score}%` }}
                      />
                    </div>
                    <span className="text-lg font-bold text-navy w-12 text-right">{score}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {results.recommendations.length > 0 && (
            <div className="bg-white border border-gray-100 p-8">
              <h3 className="text-lg font-semibold text-navy mb-2">Önerilen Eğitim Programları</h3>
              <p className="text-gray-600 mb-6 text-sm">
                Analiz sonuçlarınıza göre aşağıdaki eğitim programları öncelikli olarak önerilmektedir:
              </p>
              
              <div className="space-y-4">
                {results.recommendations.slice(0, 8).map((rec, index) => (
                  <div 
                    key={index}
                    className={`border-l-4 p-4 ${
                      rec.priority === 'critical' 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-yellow-500 bg-yellow-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`text-xs font-bold uppercase ${
                          rec.priority === 'critical' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {rec.priority === 'critical' ? 'Kritik Öncelik' : 'Önemli'}
                        </span>
                        <h4 className="font-semibold text-navy mt-1">{rec.training.name}</h4>
                        <p className="text-gray-600 text-sm mt-1">{rec.training.description}</p>
                      </div>
                      <span className="text-xs bg-navy/10 text-navy px-2 py-1 rounded">
                        {rec.category}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-gray-600 mb-4">
                  Eğitim programlarımız hakkında detaylı bilgi almak için bizimle iletişime geçin.
                </p>
                <a href="#iletisim" onClick={onBack} className="btn-primary px-8 py-3 inline-flex items-center gap-2">
                  İletişime Geç
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          )}

          {results.recommendations.length === 0 && (
            <div className="bg-white border border-gray-100 p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-2">Tebrikler!</h3>
              <p className="text-gray-600">
                Tüm kategorilerde yeterli puana sahipsiniz. Kurumunuz eğitim açısından iyi bir seviyede.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=80')`
        }}
      />
      <div className="hero-overlay absolute inset-0" />
      
      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          <p 
            className={`text-gold text-sm font-semibold tracking-widest uppercase mb-6 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.1s' }}
          >
            E&R Eğitim
          </p>
          <h1 
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.2s' }}
          >
            Bilgiyi <span className="text-gold">Davranışa</span> Dönüştürüyoruz
          </h1>
          <p 
            className={`text-lg text-gray-300 mb-10 leading-relaxed ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.3s' }}
          >
            E&R sürdürülebilir başarılara ulaşmak için gerekli yönetim ve liderlik becerilerini, 
            sürekli yenilikçilik ve mutlak ölçümleme yaklaşımıyla geliştirmek ve gerçekleştirmek 
            üzere eğitim ve danışmanlık hizmetleri sunmaktadır.
          </p>
          <div 
            className={`flex flex-col sm:flex-row gap-4 ${
              isVisible ? 'animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '0.4s' }}
          >
            <a 
              href="#egitimlerimiz" 
              className="btn-gold px-8 py-4 text-center font-semibold inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-programs"
            >
              Programları İnceleyin
              <ArrowRight className="w-5 h-5" />
            </a>
            <a 
              href="#iletisim" 
              className="border-2 border-white text-white px-8 py-4 text-center font-semibold hover:bg-white hover:text-navy transition-colors duration-300"
              data-testid="hero-cta-contact"
            >
              Kurumsal Çözümler
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-white opacity-60" />
      </div>
    </section>
  );
};

// Trust & Impact Section
const TrustSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  const stats = [
    { number: "25+", label: "Yıllık Deneyim", icon: Award },
    { number: "80+", label: "Kurumsal Referans", icon: Users },
    { number: "7", label: "Temel Değer", icon: Shield },
  ];

  return (
    <section className="py-20 bg-white" data-testid="trust-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-3 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <div 
              key={stat.label}
              className={`text-center ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: `${0.1 + index * 0.1}s` }}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-navy/5 rounded-full flex items-center justify-center">
                <stat.icon className="w-8 h-8 text-gold" />
              </div>
              <p className="text-4xl lg:text-5xl font-bold text-navy mb-2">{stat.number}</p>
              <p className="text-gray-600 text-sm font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  const values = [
    "Güvenilirlik",
    "Şeffaflık", 
    "Sürdürülebilirlik",
    "Uygulanabilirlik",
    "Ölçülebilirlik",
    "Evrensellik içinde yerellik",
    "Samimiyet"
  ];

  return (
    <section id="hakkimizda" className="py-24 lg:py-32 bg-gray-50" data-testid="about-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>
              Hakkımızda
            </p>
            <h2 className={`text-3xl lg:text-4xl font-bold text-navy mb-6 tracking-tight ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
              Çeyrek Asırlık Tecrübe ile Liderlik Geliştirme
            </h2>
            <div className="section-divider mb-8" />
            <p className={`text-gray-600 leading-relaxed mb-6 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              E&R Eğitim ve Yönetim Danışmanlığı olarak yola çıkış amacımız ekibimizin hem genel olarak 
              iş hayatında hem de eğitim sektöründe çeyrek asra yaklaşan tecrübesini yeni ve güncel bilgi 
              donanımı (know-how) ile harmanlayarak katılımcılarımıza aktarmak ve sorumlu olduğu işin 
              lideri olan çalışanlar yaratmaktır.
            </p>
            <p className={`text-gray-600 leading-relaxed ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
              Projeler, E&R tarafından literatüre kazandırılan AMETİST süreci kapsamında "yaparak öğrenme" 
              üzerine kurulu eğitim, simülasyon ve danışmanlıklar yolu ile gerçekleştirilir.
            </p>
          </div>
          <div className={`${isVisible ? 'animate-scale-in' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            <div className="bg-navy p-8 lg:p-10">
              <h3 className="text-gold text-xl font-semibold mb-6">7 Temel Değerimiz</h3>
              <ul className="space-y-4">
                {values.map((value) => (
                  <li key={value} className="flex items-center gap-3 text-white">
                    <div className="w-2 h-2 bg-gold rounded-full flex-shrink-0" />
                    <span>{value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Solutions Section
const SolutionsSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  const solutions = [
    {
      icon: Users,
      title: "Liderlik Geliştirme",
      description: "Şirketler, Yönetsel Becerileri yüksek Liderlere ihtiyaç duyarlar. Etkin iş ve insan yönetimi için kapsamlı programlar.",
      features: ["Etkin İş ve İnsan Yönetimi", "Yönetimsel Liderlik Becerileri", "Yönetim Becerileri Geliştirme"]
    },
    {
      icon: TrendingUp,
      title: "Organizasyonel Dönüşüm",
      description: "Değişimi yönetmek ve kriz dönemlerinde stratejik planlama ile kurumsal başarıyı sürdürülebilir kılmak.",
      features: ["Değişimi Yönetmek", "Kriz Yönetimi", "Hedef Odağında Stratejik Planlama"]
    },
    {
      icon: Brain,
      title: "Kurumsal Yetkinlik İnşası",
      description: "Satış, iletişim ve simülasyon alanlarında deneyimsel öğrenme metodolojisi ile yetkinlik geliştirme.",
      features: ["Aktif Satış Becerileri", "Etkin İletişim", "Çalıştay ve Webinarlar"]
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="solutions-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            Çözüm Alanlarımız
          </p>
          <h2 className={`text-3xl lg:text-4xl font-bold text-navy tracking-tight ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>
            Stratejik Gelişim Partneri
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {solutions.map((solution, index) => (
            <div key={solution.title} className={`bg-white border border-gray-100 p-8 card-hover ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: `${0.2 + index * 0.1}s` }} data-testid={`solution-card-${index}`}>
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mb-6">
                <solution.icon className="w-7 h-7 text-gold" />
              </div>
              <h3 className="text-xl font-bold text-navy mb-4">{solution.title}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{solution.description}</p>
              <ul className="space-y-2">
                {solution.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-gold rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Program Category Detail Page
const ProgramCategoryPage = ({ categoryId, onBack, onSelectCategory }) => {
  const [expandedProgram, setExpandedProgram] = useState(null);
  const category = programDetails[categoryId];
  const allCategories = Object.values(programDetails);

  if (!category) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16" data-testid="program-category-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-navy hover:text-gold transition-colors mb-8"
          data-testid="back-to-programs"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Tüm Eğitimler
        </button>

        <div className="mb-10">
          <p className="text-gold text-sm font-semibold tracking-widest uppercase mb-3">E&R Eğitim Programları</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-navy tracking-tight mb-3" data-testid="category-title">{category.title}</h1>
          <p className="text-gray-600 text-base lg:text-lg">{category.description}</p>
          <div className="w-16 h-0.5 bg-gold mt-4" />
        </div>

        {/* Category Quick Nav */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="category-nav">
          {allCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => onSelectCategory(cat.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                cat.id === categoryId
                  ? 'bg-navy text-white'
                  : 'bg-white text-navy border border-gray-200 hover:border-gold hover:text-gold'
              }`}
              data-testid={`cat-nav-${cat.id}`}
            >
              {cat.title.replace(' Eğitimleri', '').replace(' Programları', '')}
            </button>
          ))}
        </div>

        {/* Program Count */}
        <p className="text-sm text-gray-500 mb-6">{category.programs.length} eğitim programı</p>

        {/* Programs List */}
        <div className="space-y-4" data-testid="programs-list">
          {category.programs.map((program, index) => {
            const isExpanded = expandedProgram === index;
            return (
              <div
                key={index}
                className="bg-white border border-gray-100 overflow-hidden transition-shadow hover:shadow-md"
                data-testid={`program-item-${index}`}
              >
                <button
                  onClick={() => setExpandedProgram(isExpanded ? null : index)}
                  className="w-full px-6 py-5 flex items-start justify-between text-left"
                  data-testid={`program-toggle-${index}`}
                >
                  <div className="flex-1 pr-4">
                    <h3 className="text-lg font-semibold text-navy mb-1">{program.name}</h3>
                    {program.subtitle && (
                      <p className="text-sm text-gray-500 italic">{program.subtitle}</p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2">
                      {program.duration && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-2.5 py-1">
                          <Clock className="w-3.5 h-3.5 text-gold" />
                          {program.duration}
                        </span>
                      )}
                      {program.process && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-2.5 py-1">
                          <Activity className="w-3.5 h-3.5 text-gold" />
                          Süreç: {program.process}
                        </span>
                      )}
                      {program.participants && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-2.5 py-1">
                          <Users className="w-3.5 h-3.5 text-gold" />
                          {program.participants}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {isExpanded && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-5 animate-fade-in" data-testid={`program-detail-${index}`}>
                    {program.modules && program.modules.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-sm font-semibold text-navy uppercase tracking-wider mb-3">Program Modülleri</h4>
                        <div className="space-y-2">
                          {program.modules.map((mod, i) => (
                            <div key={i} className="flex items-start gap-3 pl-1">
                              <div className="w-6 h-6 bg-navy/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-xs font-bold text-gold">{i + 1}</span>
                              </div>
                              <p className="text-sm text-gray-700">{mod}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-semibold text-navy uppercase tracking-wider mb-3">
                        {categoryId === 'simulasyonlar' ? 'Simülasyon Amacı' : categoryId === 'calistay' ? 'Çalıştay Amacı' : 'Eğitim Amacı'}
                      </h4>
                      <ul className="space-y-2.5">
                        {program.objectives.map((obj, i) => (
                          <li key={i} className="flex items-start gap-3 pl-1">
                            <div className="w-1.5 h-1.5 bg-gold rounded-full flex-shrink-0 mt-2" />
                            <p className="text-sm text-gray-700 leading-relaxed">{obj}</p>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-50">
                      <a
                        href="#iletisim"
                        onClick={(e) => { e.preventDefault(); onBack(); setTimeout(() => document.getElementById('iletisim')?.scrollIntoView({behavior: 'smooth'}), 300); }}
                        className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-sm font-medium hover:bg-navy-light transition-colors"
                        data-testid={`program-contact-${index}`}
                      >
                        Bu Eğitim Hakkında Bilgi Alın
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Catalog Download Modal — Eğitim kataloğunu email karşılığında indirme
const CatalogDownloadModal = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    company_name: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/catalog/request`, formData);
      if (res.data?.status === "success") {
        // ALWAYS use same-origin API URL for the download — using a cross-origin URL
        // would cause browsers to ignore the `download` attribute and navigate away.
        const downloadUrl = `${API}/catalog/download`;
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = "E&R-Egitim-Katalog-2026.pdf";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setFormData({ email: "", full_name: "", company_name: "", phone: "" });
        }, 2500);
      } else {
        setError("Beklenmeyen bir yanıt alındı.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Bir hata oluştu, lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="catalog-modal-backdrop"
    >
      <div
        className="bg-white max-w-lg w-full rounded-sm shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="catalog-modal"
      >
        {success ? (
          <div className="p-10 text-center" data-testid="catalog-modal-success">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-navy mb-2">Kataloğunuz Hazırlanıyor</h3>
            <p className="text-gray-600 text-sm">
              İndirme otomatik olarak başlatıldı. Eğer indirme başlamadıysa{" "}
              <a
                href={`${API}/catalog/download`}
                className="text-gold hover:underline font-semibold"
                download="E&R-Egitim-Katalog-2026.pdf"
                data-testid="catalog-manual-link"
              >
                buraya tıklayın
              </a>
              .
            </p>
          </div>
        ) : (
          <>
            <div className="bg-navy px-8 py-6 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/70 hover:text-white"
                data-testid="catalog-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                  <Download className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="text-gold text-xs font-semibold tracking-wider uppercase">E&R Eğitim</p>
                  <h3 className="text-white text-lg font-bold">Eğitim Kataloğu 2026</h3>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                Kataloğumuzu ücretsiz indirmek için lütfen bilgilerinizi giriniz. PDF anında indirilmeye başlanacaktır.
              </p>

              <div>
                <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                  E-posta <span className="text-gold">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 focus:border-gold focus:outline-none transition-colors text-sm"
                  placeholder="ornek@sirket.com"
                  data-testid="catalog-email-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                    Ad Soyad
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 focus:border-gold focus:outline-none transition-colors text-sm"
                    placeholder="Adınız Soyadınız"
                    data-testid="catalog-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 focus:border-gold focus:outline-none transition-colors text-sm"
                    placeholder="0 5XX XXX XX XX"
                    data-testid="catalog-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1.5">
                  Şirket / Kurum
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 focus:border-gold focus:outline-none transition-colors text-sm"
                  placeholder="Şirket Adı"
                  data-testid="catalog-company-input"
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 text-sm px-4 py-2.5 border-l-4 border-red-400" data-testid="catalog-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="catalog-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Hazırlanıyor...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Kataloğu İndir (PDF)
                  </>
                )}
              </button>

              <p className="text-[11px] text-gray-400 text-center pt-2">
                Bilgileriniz yalnızca katalog gönderimi ve sizinle iletişim için kullanılır.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// Programs Section
const ProgramsSection = ({ onViewCategory }) => {
  const [ref, isVisible] = useScrollAnimation();
  const [catalogOpen, setCatalogOpen] = useState(false);

  const programs = [
    {
      id: "yonetim-liderlik",
      title: "Liderlik ve Yönetim Akademileri",
      subtitle: "Liderlik ve Yönetim Akademileri",
      description: "Yönetsel becerileri yüksek, güven veren liderler yetiştiriyoruz.",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80",
      icon: Users,
      programs: [
        "Yeni Nesil Yönetici: İş, İnsan ve Performans Yönetimi",
        "Mavi Yakada Liderlik ve Saha Yönetimi",
        "Anlam Mimarı — Yapay Zekâ Çağında İnsan Odaklı Liderlik",
        "Yeni Nesil Yöneticilik: Ekip, Karar ve Sorumluluk",
        "Liderler İçin Koçluk ve Gelişim Liderliği",
        "Stratejik Düşünme ve Sonuç Odaklılık",
        "Kriz ve Belirsizlikte Karar Alma",
        "Analitik Problem Çözme ve Karar Yönetimi",
        "Liderler İçin Stratejik İnsan Akademisi"
      ]
    },
    {
      id: "satis",
      title: "Satış ve Müşteri Deneyimi",
      subtitle: "Satış ve Müşteri Deneyimi Programları",
      description: "Satış bir şirketin oksijenidir. Nefes almak kadar gereklidir.",
      image: "https://images.unsplash.com/photo-1560472355-536de3962603?w=800&q=80",
      icon: TrendingUp,
      programs: [
        "Satışta Güven Kurma ve Karar Sürecini Yönetme",
        "İkna Temelli Profesyonel Satış Teknikleri",
        "AI Destekli Yeni Nesil Müşteri Kazanımı",
        "Değer Merkezli Satış Dönüşümü",
        "Gerçek Senaryolarla Satış Deneyim Atölyesi",
        "Yüksek Performanslı Satış Takımı Yönetimi",
        "Satış Müzakerelerinde Güven, Etki ve Sonuç Alma"
      ]
    },
    {
      id: "simulasyonlar",
      title: "Deneyim Atölyeleri",
      subtitle: "Deneyim Atölyeleri — Simülasyonlar",
      description: "Deneyimleyerek öğrenilenler unutulmazdır.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80",
      icon: Box,
      programs: [
        "Yüksek Performanslı Takım Deneyimi — Acil Serviste Bir Cuma Gecesi",
        "Fikirden Sonuca İnovasyon — Hayalden Gerçeğe",
        "Çevik Süreç ve Operasyon — Etkin Süreç",
        "Stratejik Kaynak Yönetimi — Kısıtlı Kaynaklar",
        "Sürdürülebilir İş Geliştirme",
        "B2B Çözüm Satışı Deneyim Simülasyonu",
        "Kurumsal Dönüşüm Yolculuğu",
        "Sınırları Yıkan İletişim Simülasyonu",
        "Rezonans — Kurum Kültürünün Yeni Frekansı"
      ]
    },
    {
      id: "kisisel-gelisim",
      title: "Organizasyonel Dönüşüm",
      subtitle: "Organizasyonel Dönüşüm Çözümleri",
      description: "Kurum kültürünü güçlendiren, bireyi ve organizasyonu birlikte geliştiren programlar.",
      image: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&q=80",
      icon: Brain,
      programs: [
        "Sonuç Odaklı Toplantı Yönetimi",
        "AI Destekli Yetkinlik Bazlı Etkin Mülakat Akademisi",
        "Müşteri Deneyiminde Mükemmellik",
        "Kuşaklararası Etkin Müzakere ve İkna Becerileri",
        "Etki Yaratan İletişim Becerileri",
        "İş Hayatında Enerji, Zaman ve Stres Yönetimi",
        "Zor Müşteri ve Durumlarla Başa Çıkma"
      ]
    },
    {
      id: "calistay",
      title: "Çalıştaylar",
      subtitle: "Çalıştay Programları",
      description: "Kısa süreli, yoğun ve uygulamalı öğrenme deneyimleri.",
      image: "https://images.unsplash.com/photo-1552581234-26160f608093?w=800&q=80",
      icon: Lightbulb,
      programs: [
        "Hakuna Matata Çalıştayı",
        "İş Hayatında Algı ve Karar Oyunları",
        "İyiden Mükemmele",
        "Propus Yıldızı",
        "Takım Potansiyeli",
        "VUCA Çağında Stratejik Düşünme",
        "İnovasyon ve Fikir Geliştirme"
      ]
    },
    {
      id: "online-egitim",
      title: "Online Eğitimler",
      subtitle: "Online Eğitim Programları",
      description: "Uzaktan erişilebilir, esnek ve etkileşimli online eğitim programları.",
      image: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800&q=80",
      icon: Activity,
      programs: [
        "Yeni Nesil Yönetici: İş, İnsan ve Performans Yönetimi Online",
        "Uçtan Uca Satış Performans Yönetimi Online",
        "Değişim Sürecinde Liderlik Online",
        "Etki Yaratan İletişim Becerileri Online",
        "Yoğun İş Akışında Odak, Öncelik ve Dayanıklılık Online",
        "Stratejik Düşünme ve Sonuç Odaklılık Online",
        "Kriz ve Belirsizlikte Karar Alma Online",
        "Yeni Nesil Yöneticilik: Ekip, Karar ve Sorumluluk Online",
        "Kuşaklararası Etkin Müzakere ve İkna Becerileri Online"
      ]
    }
  ];

  return (
    <section id="egitimlerimiz" className="py-24 lg:py-32 bg-gray-50" data-testid="programs-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>Eğitim Programları</p>
            <h2 className={`text-3xl lg:text-4xl font-bold text-navy tracking-tight mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>Kapsamlı Gelişim Programları</h2>
            <div className="section-divider" />
          </div>
          <button
            type="button"
            onClick={() => setCatalogOpen(true)}
            className={`group inline-flex items-center justify-center gap-2.5 bg-navy hover:bg-navy-light text-white px-6 py-3 text-sm font-semibold tracking-wide transition-all hover:gap-3.5 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'} self-start lg:self-end`}
            style={{ animationDelay: '0.2s' }}
            data-testid="download-catalog-btn"
          >
            <Download className="w-4 h-4 text-gold" />
            Eğitim Kataloğunu İndir
          </button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program, index) => (
            <div
              key={program.id}
              id={program.id}
              className={`group relative overflow-hidden bg-white card-hover cursor-pointer ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''} ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              data-testid={`program-card-${program.id}`}
              onClick={() => onViewCategory && onViewCategory(program.id)}
            >
              <div className={`relative ${index === 0 ? 'h-96 lg:h-full' : 'h-72'}`}>
                <img src={program.image} alt={program.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="program-card-overlay absolute inset-0" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gold/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <program.icon className="w-5 h-5 text-gold" />
                    </div>
                    <p className="text-gold text-xs font-semibold tracking-wider uppercase">{program.subtitle}</p>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{program.title}</h3>
                  <p className="text-gray-300 text-sm mb-3">{program.description}</p>
                  <div className="max-h-0 overflow-hidden transition-all duration-500 group-hover:max-h-64">
                    <ul className="space-y-1 pt-3 border-t border-white/20 mb-3">
                      {program.programs.slice(0, 4).map((item) => (
                        <li key={item} className="text-gray-300 text-xs flex items-center gap-2">
                          <div className="w-1 h-1 bg-gold rounded-full" />
                          {item}
                        </li>
                      ))}
                      {program.programs.length > 4 && <li className="text-gold text-xs">+{program.programs.length - 4} daha fazla</li>}
                    </ul>
                    <span className="inline-flex items-center gap-1.5 text-gold text-xs font-semibold">
                      Detayları Gör <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <CatalogDownloadModal open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </section>
  );
};

// Difference Section
const DifferenceSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const pillars = [
    { icon: Target, title: "Stratejik Yaklaşım", description: "Her projeyi kurumsal hedeflerle uyumlu, stratejik bir perspektifle ele alıyoruz." },
    { icon: TrendingUp, title: "Ölçülebilir Etki", description: "Mutlak ölçümleme yaklaşımıyla somut ve izlenebilir sonuçlar üretiyoruz." },
    { icon: Award, title: "Uzman Kadro", description: "Çeyrek asra yaklaşan tecrübe ve güncel know-how ile katılımcılarımıza değer katıyoruz." },
    { icon: Lightbulb, title: "Pratik Uygulama", description: "AMETİST süreci kapsamında 'yaparak öğrenme' metodolojisi uyguluyoruz." },
    { icon: Shield, title: "Sürdürülebilir Değer", description: "Sürdürülebilir başarılara ulaşmak için kalıcı yetkinlik geliştirme odaklı çalışıyoruz." }
  ];

  return (
    <section className="py-24 lg:py-32 bg-navy relative overflow-hidden" data-testid="difference-section">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
      </div>
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>Farkımız</p>
            <h2 className={`text-3xl lg:text-4xl font-bold text-white tracking-tight mb-6 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>Neden E&R?</h2>
            <div className="section-divider mb-6" />
            <p className={`text-gray-300 leading-relaxed ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
              25 yılı aşkın deneyimimizle, alanının en iyisi olan kurumsal eğitim ve danışmanlık hizmetleri sunuyoruz.
            </p>
          </div>
          <div className="lg:col-span-3 space-y-6">
            {pillars.map((pillar, index) => (
              <div key={pillar.title} className={`flex gap-5 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: `${0.3 + index * 0.1}s` }}>
                <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <pillar.icon className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{pillar.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{pillar.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// References Section
const ReferencesSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  
  // References with verified logo URLs
  const referencesWithLogos = [
    { name: "Mercedes-Benz", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/90/Mercedes-Logo.svg/200px-Mercedes-Logo.svg.png" },
    { name: "Toyota", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e7/Toyota.svg/200px-Toyota.svg.png" },
    { name: "DHL", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/DHL_Logo.svg/200px-DHL_Logo.svg.png" },
    { name: "Bosch", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/Bosch-logo.svg/200px-Bosch-logo.svg.png" },
    { name: "Roche", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/Hoffmann-La_Roche_logo.svg/200px-Hoffmann-La_Roche_logo.svg.png" },
    { name: "Novartis", logo: "https://www.novartis.com/sites/novartis_com/files/novartis-logo-og.png" },
    { name: "Pirelli", logo: "https://www.pirelli.com/tyres/tyres/wp-content/themes/developer/assets/images/pirelli-logo.png" },
    { name: "Nissan", logo: "https://www.carlogos.org/car-logos/nissan-logo-2020-black.png" },
    { name: "Braun", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Braun_Logo.svg/200px-Braun_Logo.svg.png" },
    { name: "Metro", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Metro_AG_logo.svg/200px-Metro_AG_logo.svg.png" },
    { name: "Viessmann", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Viessmann_logo.svg/200px-Viessmann_logo.svg.png" },
    { name: "GfK", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Gfk-logo.svg/200px-Gfk-logo.svg.png" },
  ];

  // All other companies as elegant text badges
  const otherReferences = [
    "Eczacıbaşı", "Koç Holding", "Doğuş Otomotiv", "Alarko", "Mey", "İDO", "Mavi Jeans", 
    "Aygaz", "Koçtaş", "Otokar", "Petkim", "ABH", "Akçansa", "Aksa", "Alvimedica", 
    "Aster", "Benteler", "Bilim İlaç", "DAP", "Doğadan", "Eko Faktoring", "Eminevim", 
    "Gürmen Group", "Huhtamaki", "IMC", "İnanlar", "Kibar Holding", "Kimteks", "Kipa", 
    "Koç Ticaret", "Konya Çimento", "Kordsa", "Korozo", "Liberty", "Li & Fung", "Mopsan", 
    "Omsan", "Quattro", "Sanko Makina", "SecretCV", "Silkar", "Silverline", "Sinpaş", 
    "Söktaş", "Star Pet", "Tat", "Topkapı", "Türk Tuborg", "Uzmar Group", "Vakıf Emeklilik", 
    "Viking", "YDS", "Zimmer", "Ziraat Emeklilik", "Koç Üniversitesi", "Oriflame",
    "Borsa Istanbul", "TÜBİTAK", "Şişecam"
  ];

  const [loadedLogos, setLoadedLogos] = useState({});

  const handleImageError = (name) => {
    setLoadedLogos(prev => ({ ...prev, [name]: false }));
  };

  const handleImageLoad = (name) => {
    setLoadedLogos(prev => ({ ...prev, [name]: true }));
  };

  return (
    <section id="referanslar" className="py-24 lg:py-32 bg-white" data-testid="references-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>Referanslar</p>
          <h2 className={`text-3xl lg:text-4xl font-bold text-navy tracking-tight mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>Güvenilir İş Ortaklıkları</h2>
          <p className={`text-gray-600 max-w-2xl mx-auto ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>Türkiye'nin ve dünyanın önde gelen kurumlarına eğitim ve danışmanlık hizmetleri sunmaktan gurur duyuyoruz.</p>
        </div>
        
        {/* Featured logos */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 mb-12 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
          {referencesWithLogos.map((company, index) => (
            <div 
              key={company.name} 
              className="bg-white border border-gray-200 rounded-lg p-6 flex items-center justify-center h-28 hover:border-gold hover:shadow-lg transition-all duration-300 group"
              data-testid={`reference-logo-${index}`}
              title={company.name}
            >
              {loadedLogos[company.name] === false ? (
                <span className="text-navy font-semibold text-sm text-center">{company.name}</span>
              ) : (
                <img 
                  src={company.logo} 
                  alt={company.name}
                  className="max-h-12 max-w-full object-contain opacity-70 group-hover:opacity-100 transition-all duration-300"
                  loading="lazy"
                  onError={() => handleImageError(company.name)}
                  onLoad={() => handleImageLoad(company.name)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Other references as elegant badges */}
        <div className={`border-t border-gray-100 pt-10 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.5s' }}>
          <p className="text-center text-sm text-gray-500 mb-8 font-medium">Ve daha birçok değerli kurum...</p>
          <div className="flex flex-wrap justify-center gap-3">
            {otherReferences.map((company) => (
              <span 
                key={company} 
                className="text-navy/70 text-sm font-medium hover:text-gold hover:bg-gold/5 transition-all duration-300 px-4 py-2 bg-gray-50 rounded-full border border-gray-100 hover:border-gold/30"
              >
                {company}
              </span>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className={`mt-16 text-center ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.7s' }}>
          <p className="text-5xl font-bold text-gold mb-2">80+</p>
          <p className="text-navy font-medium">Kurumsal Referans</p>
        </div>
      </div>
    </section>
  );
};

// Articles Section
const ArticlesSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const articles = [
    { title: "Çalışan Eğitiminin Öncesi ve Sonrasına Dair", category: "Eğitim" },
    { title: "Yeni Zamanlarda Satışta Algı Yönetimi", category: "Satış" },
    { title: "Yeni Zamanlarda Yetişkin Eğitimine Dair", category: "Eğitim" },
    { title: '"Artık Şirket Karlılığından Yöneticiler Sorumlu Olacaklar…"', category: "Yönetim" },
    { title: "Şirketlerde Geri Bildirim Yönetimi Ve Koçluğa Dair", category: "Koçluk" },
    { title: 'Satış Ekibini "Koç Gibi" Yönetmek', category: "Satış" },
    { title: 'Lider\'de Etiket "İtibar kazanmak 20 yıl sürer, onu mahvetmek ise sadece 5 dakika."', category: "Liderlik" },
    { title: 'Çalışan Uyumu, X - Y Denklemi "Şu gençliğin hali ne olacak"', category: "İK" },
    { title: "Potansiyel-Engeller=Performans", category: "Performans" },
    { title: "Karar Alma Sürecinde Yönetici/Lider", category: "Liderlik" }
  ];

  return (
    <section id="makaleler" className="py-24 lg:py-32 bg-gray-50" data-testid="articles-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>Makaleler</p>
          <h2 className={`text-3xl lg:text-4xl font-bold text-navy tracking-tight mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>Liderlik Görüşleri</h2>
          <div className="section-divider" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article, index) => (
            <div key={article.title} className={`bg-white border border-gray-100 p-6 card-hover ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`} style={{ animationDelay: `${0.2 + index * 0.05}s` }} data-testid={`article-card-${index}`}>
              <span className="inline-block bg-gold/10 text-gold text-xs font-semibold px-3 py-1 mb-4">{article.category}</span>
              <h3 className="text-navy font-semibold leading-snug hover:text-gold transition-colors duration-300 cursor-pointer">{article.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSent(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setSent(false), 5000);
    } catch {
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="iletisim" className="py-24 lg:py-32 bg-white" data-testid="contact-section">
      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className={`text-gold text-sm font-semibold tracking-widest uppercase mb-4 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>İletişim</p>
            <h2 className={`text-3xl lg:text-4xl font-bold text-navy tracking-tight mb-6 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`} style={{ animationDelay: '0.1s' }}>Bize Ulaşın</h2>
            <div className="section-divider mb-8" />
            <div className="space-y-6">
              <div className={`flex gap-4 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
                <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-gold" /></div>
                <div><h4 className="font-semibold text-navy mb-1">Adres</h4><p className="text-gray-600 text-sm leading-relaxed">Esentepe, Büyükdere Cad. Emekli Subay Evleri<br />B: 9 D: 3 Şişli / Istanbul / Türkiye</p></div>
              </div>
              <div className={`flex gap-4 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
                <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-gold" /></div>
                <div><h4 className="font-semibold text-navy mb-1">Telefon</h4><p className="text-gray-600 text-sm">+(90) 212 274 0 ENR (274 0 367)</p><p className="text-gray-500 text-sm">Faks: +(90) 212 275 1 357</p></div>
              </div>
              <div className={`flex gap-4 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
                <div className="w-12 h-12 bg-navy/5 rounded-full flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-gold" /></div>
                <div><h4 className="font-semibold text-navy mb-1">E-posta</h4><a href="mailto:info@enr.com.tr" className="text-gold hover:underline text-sm">info@enr.com.tr</a></div>
              </div>
            </div>
          </div>
          <div className={`bg-gray-50 p-8 lg:p-10 ${isVisible ? 'animate-scale-in' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            <h3 className="text-xl font-bold text-navy mb-6">Mesaj Gönderin</h3>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div><label className="block text-sm font-medium text-navy mb-2">Adınız Soyadınız *</label><input type="text" required className="w-full px-4 py-3 border border-gray-200 bg-white text-navy" placeholder="Adınız Soyadınız" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="input-name" /></div>
              <div><label className="block text-sm font-medium text-navy mb-2">E-posta Adresiniz *</label><input type="email" required className="w-full px-4 py-3 border border-gray-200 bg-white text-navy" placeholder="ornek@sirket.com" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="input-email" /></div>
              <div><label className="block text-sm font-medium text-navy mb-2">Telefonunuz</label><input type="tel" className="w-full px-4 py-3 border border-gray-200 bg-white text-navy" placeholder="+90 5XX XXX XX XX" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="input-phone" /></div>
              <div><label className="block text-sm font-medium text-navy mb-2">Mesajınız *</label><textarea required rows={4} className="w-full px-4 py-3 border border-gray-200 bg-white text-navy resize-none" placeholder="Mesajınızı buraya yazın..." value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} data-testid="input-message" /></div>
              <button type="submit" disabled={sending} className="w-full btn-gold py-4 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-button">
                {sent ? 'Mesajınız Gönderildi!' : sending ? 'Gönderiliyor...' : 'Mesaj Gönder'}{!sent && !sending && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ onAdminClick }) => (
  <footer className="bg-navy py-12" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8 mb-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_1bd27347-3044-4a39-a075-7e98dbe475af/artifacts/dpjq0y2o_ChatGPT%20Image%205%20Mar%202026%2013_58_12.png" 
              alt="E&R Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div><p className="text-white font-semibold text-sm">E&R EĞİTİM</p><p className="text-gold text-xs">Yönetim Danışmanlığı</p></div>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed max-w-md">Sürdürülebilir başarılara ulaşmak için gerekli yönetim ve liderlik becerilerini, sürekli yenilikçilik ve mutlak ölçümleme yaklaşımıyla geliştiriyoruz.</p>
        </div>
        <div><h4 className="text-white font-semibold mb-4">Hızlı Bağlantılar</h4><ul className="space-y-2">{["Hakkımızda", "Eğitimlerimiz", "Referanslar", "İletişim"].map((link) => (<li key={link}><a href={`#${link.toLowerCase().replace(/ı/g, 'i').replace(/ş/g, 's')}`} className="text-gray-400 text-sm hover:text-gold transition-colors duration-300">{link}</a></li>))}</ul></div>
        <div><h4 className="text-white font-semibold mb-4">İletişim</h4><ul className="space-y-2 text-gray-400 text-sm"><li>Şişli / Istanbul / Türkiye</li><li>+(90) 212 274 0 367</li><li><a href="mailto:info@enr.com.tr" className="hover:text-gold transition-colors duration-300">info@enr.com.tr</a></li></ul></div>
      </div>
      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-sm">Copyright © {new Date().getFullYear()} enr.com.tr - Tüm hakları saklıdır.</p>
        {onAdminClick && (
          <button onClick={onAdminClick} className="text-gray-600 text-xs hover:text-gray-400 transition-colors" data-testid="admin-link">
            Yönetim
          </button>
        )}
      </div>
    </div>
  </footer>
);

// URL <-> activeSection eslestirmeleri (reklam/SEO icin direkt URL'ler)
const PATH_TO_SECTION = {
  "/": null,
  "/testler": "tests",
  "/egitim-ihtiyac-analizi": "needs-analysis",
  "/admin": "admin",
};
const SECTION_TO_PATH = {
  null: "/",
  tests: "/testler",
  "needs-analysis": "/egitim-ihtiyac-analizi",
  admin: "/admin",
};
const PAGE_TITLES = {
  "/": "E&R Eğitim - Yönetim Danışmanlığı ve Kurumsal Eğitim",
  "/testler": "Online Testler - E&R Eğitim",
  "/egitim-ihtiyac-analizi": "Eğitim İhtiyaç Analizi - E&R Eğitim",
  "/admin": "Admin Panel - E&R Eğitim",
};

// Main App Component
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSectionState] = useState(() => {
    const fromPath = PATH_TO_SECTION[location.pathname];
    return fromPath !== undefined ? fromPath : null;
  });
  const [activeProgramCategory, setActiveProgramCategory] = useState(null);

  // URL degistiginde (back/forward, direct link) state'i senkronize et
  useEffect(() => {
    const section = PATH_TO_SECTION[location.pathname];
    if (section !== undefined) {
      setActiveSectionState(section);
      if (section !== "program-detail") {
        setActiveProgramCategory(null);
      }
    }
    // Sayfa basligi
    const title = PAGE_TITLES[location.pathname] || PAGE_TITLES["/"];
    document.title = title;
    // Yeni sayfaya gecince yukari kaydir
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // setActiveSection wrapper'i: hem state'i hem URL'i guncelle
  const setActiveSection = (section) => {
    setActiveSectionState(section);
    const targetPath = SECTION_TO_PATH[section];
    if (targetPath && location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect PayTR return redirect and navigate to tests page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment');
    if (paymentResult) {
      setActiveSection('tests');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewCategory = (categoryId) => {
    setActiveProgramCategory(categoryId);
    setActiveSection('program-detail');
    window.scrollTo(0, 0);
  };

  if (activeSection === 'admin') {
    return <AdminPanel onBack={() => setActiveSection(null)} />;
  }

  if (activeSection === 'program-detail' && activeProgramCategory) {
    return (
      <div className="App font-inter">
        <Navbar scrolled={true} activeSection={activeSection} setActiveSection={setActiveSection} />
        <ProgramCategoryPage
          categoryId={activeProgramCategory}
          onBack={() => { setActiveSection(null); setActiveProgramCategory(null); }}
          onSelectCategory={(id) => { setActiveProgramCategory(id); window.scrollTo(0, 0); }}
        />
        <Footer onAdminClick={() => setActiveSection('admin')} />
      </div>
    );
  }

  if (activeSection === 'tests') {
    return (
      <div className="App font-inter">
        <Navbar scrolled={true} activeSection={activeSection} setActiveSection={setActiveSection} />
        <TestsPage onBack={() => setActiveSection(null)} />
        <Footer onAdminClick={() => setActiveSection('admin')} />
      </div>
    );
  }

  if (activeSection === 'needs-analysis') {
    return (
      <div className="App font-inter">
        <Navbar scrolled={true} activeSection={activeSection} setActiveSection={setActiveSection} />
        <NeedsAnalysisPage onBack={() => setActiveSection(null)} />
        <Footer onAdminClick={() => setActiveSection('admin')} />
      </div>
    );
  }

  return (
    <div className="App font-inter">
      <Navbar scrolled={scrolled} activeSection={activeSection} setActiveSection={setActiveSection} />
      <HeroSection />
      <TrustSection />
      <AboutSection />
      <SolutionsSection />
      <ProgramsSection onViewCategory={handleViewCategory} />
      <DifferenceSection />
      <ReferencesSection />
      <ContactSection />
      <Footer onAdminClick={() => setActiveSection('admin')} />
    </div>
  );
}

export default App;
